import { z } from 'zod'
import { oc } from '@orpc/contract'
import { identityMethodSchema, identityStatusSchema } from './patient-identity.contract'

// Patient-facing identity verification (docs/PERSONA_IDV_PLAN.md §7): the authenticated patient's own
// status, consent, and starting or checking a Persona attempt. No input names a patient or an
// inquiry — every call acts on the caller, and the inquiry binding lives server-side (§4 rule 5).

const myIdentityVerificationSchema = z.object({
  status: identityStatusSchema,
  method: identityMethodSchema.optional(),
  verifiedAt: z.string().optional(),
  /** Whether **Verify now** is offered to this patient at all (cohort, flag, configuration). */
  personaOffered: z.boolean(),
  /** Consent to the current consent text exists — `start` refuses without it. */
  consentGiven: z.boolean(),
  /** `start` would succeed now: offered, consented, and an open attempt to resume or retries left. */
  canStart: z.boolean(),
  /**
   * An attempt is started and not yet decided: `start` resumes it (a fresh link to the same inquiry)
   * rather than starting another, so the card says "in progress" and offers **Continue**.
   */
  attemptInProgress: z.boolean(),
})

// `/status`, not the bare `/v2/patient/me/identity-verification`: that path is also the staff
// `GET /v2/patient/{id}/identity-verification` with `id = "me"` (see `orpc/contracts.spec.ts`).
export const getMyIdentityVerification = oc
  .route({ method: 'GET', path: '/v2/patient/me/identity-verification/status' })
  .output(myIdentityVerificationSchema)

export const giveMyIdentityVerificationConsent = oc
  .route({ method: 'POST', path: '/v2/patient/me/identity-verification/consent' })
  .output(myIdentityVerificationSchema)

// Returns only the one-time link to Persona's hosted flow (D2): a credential, never stored or logged.
// The portal opens it here (**Verify now**) or shows it as a QR code for another device.
// CONFLICT when no link can be given now, with `data.reason` saying why: `submitted` — the previous
// attempt is with Persona and its result has not come back, so starting another would make the
// patient verify twice; `decided` — that result just came in (read the status again); `starting` —
// another start for the patient is still running.
export const startMyIdentityVerification = oc
  .route({ method: 'POST', path: '/v2/patient/me/identity-verification/start' })
  .errors({
    CONFLICT: { data: z.object({ reason: z.enum(['submitted', 'decided', 'starting']) }) },
  })
  .output(z.object({ url: z.string() }))

// The return page's "look now": syncs the caller's open attempt, then answers like the GET.
export const checkMyIdentityVerification = oc
  .route({ method: 'POST', path: '/v2/patient/me/identity-verification/check' })
  .output(myIdentityVerificationSchema)

// **Verify with our team**: journals the patient's choice (`manual_requested`) and moves nothing —
// asking for manual verification does not verify anyone. The Care Queue task follows in step 6.1.
export const requestMyManualIdentityVerification = oc
  .route({ method: 'POST', path: '/v2/patient/me/identity-verification/manual-request' })
  .output(myIdentityVerificationSchema)
